import { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import toColorString from 'polished/lib/color/toColorString';
import { Trans, t } from '@lingui/macro';
import { format, parseISO } from 'date-fns';

import colors from '@decisiv/design-tokens/lib/color';

import ExclamationTriangle from '@decisiv/iconix/lib/components/ExclamationTriangle';
import ExternalLink from '@decisiv/iconix/lib/components/ExternalLink';
import Revert from '@decisiv/iconix/lib/components/Revert';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Button from '@decisiv/ui-components/lib/components/Button';
import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import type { TranslatedText } from '@decisiv/ui-components/lib/utils/useTranslations';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';

import LogoImage from 'components/LogoImage';
import HighlightText from 'components/HighlightedText';

import { useTeams } from 'pages/Teams/contexts/TeamsContext';

import { useSearchContext } from 'contexts/SearchContext';
import { buildTeamPath } from 'utils/teamFromUrl';
import restoreFromDeletionMutation from './restoreTeamFromDeletionMutation.graphql';

const UnderlineButton = styled(Button)`
  text-decoration: underline;
  text-transform: inherit;
  justify-content: flex-start;
  height: inherit;
` as typeof Button;

const formatDate = (date: string) => {
  if (!date) return undefined;

  return format(parseISO(date), 'h:mm aa');
};

type Props = {
  rowData: Pick<
    SentinelTeam,
    | 'name'
    | 'applicationName'
    | 'logo'
    | 'id'
    | 'teamKey'
    | 'scheduledForDeletionAt'
  >;
  hideLogo?: boolean;
  openInNewTab?: boolean;
  disabled?: boolean;
  /**
   * Explanation shown as a tooltip on the disabled team name. Only rendered
   * when the name is disabled via the `disabled` prop, and never for a team
   * scheduled for deletion: the reason it cannot be opened is the pending
   * deletion, so a "no accounts" tooltip would be misleading advice there.
   */
  disabledReason?: TranslatedText;
};

export default function NameCell({
  rowData: { name, applicationName, logo, id, teamKey, scheduledForDeletionAt },
  hideLogo = false,
  openInNewTab = false,
  disabled = false,
  disabledReason,
}: Props) {
  const buttonRef = useRef(null);
  const nameRef = useRef(null);
  const { notify } = useNotifications();

  const { searchString } = useSearchContext();

  const { environment } = useTeams() as any;

  const [restoreFromDeletion, { loading }] = useMutation(
    restoreFromDeletionMutation,
    {
      onError: /* istanbul ignore next -- @preserve */ (err) => {
        /* istanbul ignore next -- @preserve */
        notify(
          {
            id: err.name,
            duration: 5000,
            onClose: () => undefined,
            intent: 'danger',
            title: t`Sentinel Team Not Restored`,
          },
          <>{err.message}</>,
        );
      },
    },
  );

  const timeToDelete = useMemo(
    () => formatDate(scheduledForDeletionAt),
    [scheduledForDeletionAt],
  );

  const showRecoverTeamButton =
    !!environment && environment !== 'production' && scheduledForDeletionAt;

  const deleteWarningString = t`Will be deleted at ${timeToDelete}.`;

  /* istanbul ignore next -- @preserve */
  const handleOnRestoreTeam = useCallback(() => {
    restoreFromDeletion({ variables: { id } });
  }, [restoreFromDeletion, id]);

  return (
    <Flex alignItems="center">
      {!hideLogo && (
        <Flex marginRight={1} data-testid="team-logo">
          <LogoImage src={logo?.url} />
        </Flex>
      )}
      <Flex flexDirection="column">
        {disabled && !!disabledReason && !scheduledForDeletionAt && (
          <Tooltip placement="right" zIndex={5} target={nameRef}>
            {disabledReason}
          </Tooltip>
        )}
        {/* The tooltip targets this wrapper rather than the button itself,
            because a disabled button does not emit mouse events. */}
        <Flex ref={nameRef}>
          <UnderlineButton
            text={<HighlightText text={name} searchText={searchString} />}
            variant="ghost"
            icon={openInNewTab ? ExternalLink : undefined}
            disabled={!!scheduledForDeletionAt || disabled}
            onClick={async () => {
              if (openInNewTab) {
                window.open(
                  buildTeamPath(teamKey, '/'),
                  '_blank',
                  'noopener, noreferrer',
                );
              } else {
                window.open(buildTeamPath(teamKey, '/'), '_self');
              }
            }}
          />
        </Flex>
        <P as="span" size="small" shade={1}>
          {applicationName}
        </P>
        {!!showRecoverTeamButton && (
          <Flex>
            <Flex>
              <ExclamationTriangle
                color={toColorString(colors.status.danger.medium)}
              />
            </Flex>
            <P size="small" marginX={0.5} color="alaskanHusky">
              {deleteWarningString}
            </P>
            <Tooltip placement="right" zIndex={5} target={buttonRef}>
              <Trans>Recover Team</Trans>
            </Tooltip>
            <Button
              ref={buttonRef}
              icon={Revert}
              variant="ghost"
              disabled={loading}
              onClick={handleOnRestoreTeam}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
